import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Play, ChevronDown, ChevronRight, Trash2, Terminal } from "lucide-react";
import { useAgent } from "@/api/queries/agents";
import { useExecAgent, RUN_AS_USERS, type RunAsUser } from "@/api/mutations/agentExec";
import { SectionCard } from "@/components/SectionCard";
import { BackLink } from "@/components/DetailHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { splitCmd, parseJsonCmd, parseEnvJson } from "@/lib/parseCmd";
import type { ExecResult } from "@/types";

const HISTORY_LIMIT = 20;
const DEFAULT_TIMEOUT_MS = 30_000;

interface HistoryEntry {
  key: number;
  cmd: string[];
  ts: string;
  result?: ExecResult;
  error?: string;
  expanded: boolean;
}

function ResultDisplay({ result }: { result: ExecResult }) {
  const isError = result.exitCode === null || result.exitCode !== 0;
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-2">
        <Badge variant={isError ? "revoked" : "active"} data-testid="exec-exit-code">
          exit={result.exitCode ?? "null"}
        </Badge>
        <Badge variant="outline">{result.durationMs}ms</Badge>
        <Badge variant="outline">runAs={result.runAs}</Badge>
        {result.truncated && <Badge variant="revoked">truncated</Badge>}
        {result.timedOut && <Badge variant="revoked">timeout</Badge>}
      </div>
      {result.stdout && (
        <div>
          <div className="mb-1 text-text3">stdout</div>
          <pre className="mono max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-md bg-surface2 p-2.5 text-text2">
            {result.stdout}
          </pre>
        </div>
      )}
      {result.stderr && (
        <div>
          <div className="mb-1 text-danger">stderr</div>
          <pre className="mono max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-md bg-surface2 p-2.5 text-danger">
            {result.stderr}
          </pre>
        </div>
      )}
      {!result.stdout && !result.stderr && (
        <div className="italic text-text3">(kein Output)</div>
      )}
    </div>
  );
}

export function AgentExec() {
  const { id } = useParams<{ id: string }>();
  const agentQuery = useAgent(id);
  const mutation = useExecAgent();

  const [cmdInput, setCmdInput] = useState("");
  const [advancedMode, setAdvancedMode] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [timeoutMs, setTimeoutMs] = useState<string>(String(DEFAULT_TIMEOUT_MS));
  const [envJson, setEnvJson] = useState("");
  const [runAs, setRunAs] = useState<RunAsUser>("agent-mesh-admin");
  const [parseError, setParseError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  if (!id) return null;

  function handleRun() {
    setParseError(null);

    let cmd: string[];
    if (advancedMode) {
      const r = parseJsonCmd(cmdInput);
      if (!r.ok) {
        setParseError(r.error ?? "Parse-Fehler");
        return;
      }
      cmd = r.value!;
    } else {
      cmd = splitCmd(cmdInput);
      if (cmd.length === 0) {
        setParseError("cmd ist leer");
        return;
      }
    }

    const envResult = parseEnvJson(envJson);
    if (!envResult.ok) {
      setParseError(envResult.error);
      return;
    }

    const timeoutNum = Number(timeoutMs);
    const timeout = Number.isFinite(timeoutNum) && timeoutNum > 0 ? timeoutNum : undefined;

    const key = Date.now();
    const entry: HistoryEntry = { key, cmd, ts: new Date().toISOString(), expanded: true };

    setHistory((prev) => {
      const collapsed = prev.map((e) => ({ ...e, expanded: false }));
      return [entry, ...collapsed].slice(0, HISTORY_LIMIT);
    });

    mutation.mutate(
      { agentId: id!, cmd, timeoutMs: timeout, env: envResult.value, runAs },
      {
        onSuccess: (result) => {
          setHistory((prev) => prev.map((e) => (e.key === key ? { ...e, result } : e)));
          const ok = result.exitCode === 0;
          if (ok) toast.success(`exit=${result.exitCode}, ${result.durationMs}ms`);
          else toast.error(`exit=${result.exitCode ?? "null"}, ${result.durationMs}ms`);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setHistory((prev) => prev.map((e) => (e.key === key ? { ...e, error: msg } : e)));
          toast.error(`Exec fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  function toggleEntry(key: number) {
    setHistory((prev) => prev.map((e) => (e.key === key ? { ...e, expanded: !e.expanded } : e)));
  }

  return (
    <div className="space-y-5">
      <BackLink to={`/agents/${id}`} label={agentQuery.data?.name ?? "Agent"} />

      <div>
        <h1 className="m-0 flex items-center gap-2 text-[21px] font-extrabold tracking-tight text-text">
          <Terminal className="h-[22px] w-[22px] text-accent" />
          Exec-Konsole: {agentQuery.data?.name ?? id}
        </h1>
        <p className="mt-1 text-[13px] text-text2">
          Führt ein Kommando im Brain-Container aus. Admin- oder Owner-Self-Permission nötig.
        </p>
      </div>

      <SectionCard
        title="Command"
        description={advancedMode ? 'JSON-Array wie ["echo", "hello world"]' : "Whitespace-separated, kein Shell-Quoting"}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="exec-cmd">Cmd</Label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-text3">
                <input
                  type="checkbox"
                  checked={advancedMode}
                  onChange={(e) => setAdvancedMode(e.target.checked)}
                  className="accent-accent"
                  data-testid="exec-advanced-toggle"
                />
                Advanced (JSON-Array)
              </label>
            </div>
            {advancedMode ? (
              <Textarea
                id="exec-cmd"
                value={cmdInput}
                onChange={(e) => setCmdInput(e.target.value)}
                rows={3}
                className="font-mono text-xs"
                placeholder='["echo", "hello world"]'
                data-testid="exec-cmd-input"
              />
            ) : (
              <Input
                id="exec-cmd"
                value={cmdInput}
                onChange={(e) => setCmdInput(e.target.value)}
                placeholder="whoami"
                className="font-mono"
                data-testid="exec-cmd-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !mutation.isPending) {
                    e.preventDefault();
                    handleRun();
                  }
                }}
              />
            )}
          </div>

          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-text3 hover:text-text"
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="exec-advanced-options-toggle"
          >
            {advancedOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Optionen (runAs, timeout, env)
          </button>

          {advancedOpen && (
            <div className="space-y-2 border-l border-border pl-4">
              <div className="space-y-1">
                <Label>runAs (Container-User)</Label>
                <Select value={runAs} onValueChange={(v) => setRunAs(v as RunAsUser)}>
                  <SelectTrigger className="w-64" data-testid="exec-runas-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RUN_AS_USERS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                        {u === "agent-mesh-admin" ? " (default, admin)" : ""}
                        {u === "claude" ? " (Brain-User, für Plugin-Install)" : ""}
                        {u === "agent-mesh-user" ? " (owner-Pfad)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-text3">
                  Default <code>agent-mesh-admin</code>. <code>claude</code> für{" "}
                  <code>claude plugin install</code> o.ä.
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="exec-timeout">Timeout (ms, max 300000)</Label>
                <Input
                  id="exec-timeout"
                  type="number"
                  value={timeoutMs}
                  onChange={(e) => setTimeoutMs(e.target.value)}
                  className="w-48 font-mono"
                  data-testid="exec-timeout-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="exec-env">Env (JSON-Object, optional)</Label>
                <Textarea
                  id="exec-env"
                  value={envJson}
                  onChange={(e) => setEnvJson(e.target.value)}
                  rows={3}
                  className="font-mono text-xs"
                  placeholder='{"FOO": "bar"}'
                  data-testid="exec-env-input"
                />
              </div>
            </div>
          )}

          {parseError && (
            <div className="text-sm text-danger" data-testid="exec-parse-error">{parseError}</div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleRun} disabled={mutation.isPending || cmdInput.trim().length === 0} data-testid="exec-run">
              <Play className="h-4 w-4" />
              {mutation.isPending ? "Running…" : "Run"}
            </Button>
          </div>
        </div>
      </SectionCard>

      {history.length > 0 && (
        <SectionCard
          testId="exec-history"
          title={`History (${history.length})`}
          action={
            <Button variant="ghost" size="sm" onClick={() => setHistory([])} data-testid="exec-history-clear">
              <Trash2 className="h-4 w-4" />Clear
            </Button>
          }
          noPad
        >
          {history.map((entry) => {
            const ok = entry.result?.exitCode === 0;
            return (
              <div
                key={entry.key}
                className="border-b border-border last:border-0"
                data-testid={`exec-history-entry-${entry.key}`}
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-surface-hover"
                  onClick={() => toggleEntry(entry.key)}
                >
                  {entry.expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  <code className="mono flex-1 truncate text-xs">{entry.cmd.join(" ")}</code>
                  {entry.error && <Badge variant="revoked">error</Badge>}
                  {entry.result && (
                    <Badge variant={ok ? "active" : "revoked"}>exit={entry.result.exitCode ?? "null"}</Badge>
                  )}
                  {!entry.result && !entry.error && (
                    <span className="animate-pulse text-xs text-text3">running…</span>
                  )}
                </button>
                {entry.expanded && (
                  <div className="px-4 pb-3">
                    {entry.error && (
                      <div className="text-sm text-danger" data-testid="exec-history-error">{entry.error}</div>
                    )}
                    {entry.result && <ResultDisplay result={entry.result} />}
                  </div>
                )}
              </div>
            );
          })}
        </SectionCard>
      )}
    </div>
  );
}
